import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { request, gql } from "graphql-request";
import { hyGraphToken, endpointURL } from "../../ThemeModule";
import CaseStudyPageHeader from './CaseStudyPageHeader';
import { ParallaxBanner } from "react-scroll-parallax";
import Seo from "../../components/Seo";
import { SITE_URL, breadcrumbSchema, toDescription } from "../../seo/siteMeta.mjs";

// Case study bodies may still hold placeholder copy, so fall back to a
// title-based description rather than publishing filler text.
const caseStudyDescription = (post) =>
  toDescription(post.content?.text, {
    fallback: `${post.title.trim()} — a Dark Defensive case study on cyber risk assessment, compliance, and building operational resilience.`,
  });

const CaseStudiesDetails = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      const endpoint = endpointURL;
      const query = gql`
        query GetCasestudyBySlug($slug: String!) {
          caseStudy(where: { slug: $slug }) {
            title
            slug
            publishedAt
            updatedAt
            content {
              html
              text
            }
            featureImage {
              url
            }
            bannerImage {
              url
            }
          }
        }
      `;

      const headers = {
        Authorization: `Bearer ${hyGraphToken}`,
      };

      try {
        const data = await request(endpoint, query, { slug }, headers);
        setPost(data.caseStudy);
        console.log(data.caseStudy);
      } catch (error) {
        console.error("Error fetching post:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  return (
    <>
      {!loading && !post && (
        <Seo
          path={`/case-study/${slug}`}
          title="Case Study Not Found"
          description="This case study is no longer available."
          noindex
        />
      )}
      {post && (
        <Seo
          path={`/case-study/${slug}`}
          title={post.title.trim()}
          description={caseStudyDescription(post)}
          type="article"
          image={post.featureImage?.url || post.bannerImage?.url}
          imageAlt={post.title.trim()}
          publishedTime={post.publishedAt}
          modifiedTime={post.updatedAt || post.publishedAt}
          schema={[
            {
              "@type": "Article",
              "@id": `${SITE_URL}/case-study/${slug}#article`,
              headline: post.title.trim(),
              description: caseStudyDescription(post),
              image: post.featureImage?.url ? [post.featureImage.url] : undefined,
              datePublished: post.publishedAt,
              dateModified: post.updatedAt || post.publishedAt,
              author: { "@id": `${SITE_URL}/#organization` },
              publisher: { "@id": `${SITE_URL}/#organization` },
              mainEntityOfPage: `${SITE_URL}/case-study/${slug}`,
            },
            breadcrumbSchema([
              { name: "Home", path: "/" },
              { name: "Case Studies", path: "/case-studies" },
              { name: post.title.trim(), path: `/case-study/${slug}` },
            ]),
          ]}
        />
      )}

      <CaseStudyPageHeader 
        post={post}
        title={post ? post.title : "Loading..."}
      />
      {loading ? (
        <div className="loader text-center">Loading...</div>
      ) : error ? (
        <div className="error-message text-red-500 text-center">
          <p>Failed to load post: {error}</p>
        </div>
      ) : (
        post && (
          <div className="post-details">
            <ParallaxBanner
              layers={[{ image: post.bannerImage.url, speed: -15 }]}
              className="aspect-[3/1]"
            />
            <div className="max-w-container shape-1 shape-2 relative mx-auto px-4 pt-24 pb-20">
              <div className="mx-auto flex justify-center lg:px-5 lg:-mx-4">
                <div className="w-full lg:w-8/12 px-6">
                  <div
                    className="post-content relative"
                    dangerouslySetInnerHTML={{ __html: post.content.html }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        )
      )}
    </>
  );
};

export default CaseStudiesDetails;
